/* eslint-disable no-unused-expressions */
/* global window */
/* eslint-disable jsx-a11y/anchor-is-valid */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortBy, findIndex, get, map, split } from 'lodash';
import { withTranslate } from 'react-redux-multilingual';
import Loader from 'components/Loader/Loader';
import SelectDropDown from 'components/SelectDropdown/SelectDropdown';
import { requestWatchlistNames, requestWatchlist, watchlistTabs } from 'Actions/watchlistActions';
import SlideToggle from 'components/SlideToggle/SlideToggle';
import ChangeDir from 'components/ChangeDir/ChangeDir';
import Icon from 'components/Icon/Icon';
import { getRoute } from 'Utils/symbolRedirectHelper';
import './MyWatchListsWidget.scss';

const { format } = window;

const MyWatchListsWidget = props => {
	const { translate } = props;
	const dispatch = useDispatch();
	const watchlist = useSelector(state => state.watchlistData.watchlist);
	const fetchingWatchlist = useSelector(state => state.watchlistData.fetchingWatchlist);
	const currentCurrency = useSelector(state => state.watchlistData.currentCurrency);
	const activeWatchlistId = useSelector(state => state.watchlistData.activeWatchlistId);
	const user = useSelector(state => state.userData.user);
	const xref = useSelector(state => state.xrefData.data);

	const [watchlists, setWatchlists] = useState([]);
	const [selectedWatchlistIndex, setSelectedWatchlistIndex] = useState(0);
	const [showChangeInDollars, setShowChangeInDollars] = useState(true);
	const [limitResultsToTen, setLimitResultsToTen] = useState(true);

	useEffect(() => {
		dispatch(requestWatchlistNames(false, lists => {
			const sortedWatchlists = sortBy(lists.data, list => list.name !== '__RECENT__');
			const defaultWatchlistIndex = findIndex(
				sortedWatchlists,
				currentWatchlist => currentWatchlist.defaultPortfolio === true
			);
			setWatchlists(sortedWatchlists);
			if (defaultWatchlistIndex !== -1) {
				setSelectedWatchlistIndex(defaultWatchlistIndex);
			}
		}));
	}, []);

	useEffect(() => {
		if (watchlists.length === 0) {
			return;
		}
		const { portfolioId, isRecentQuotes } = watchlists[selectedWatchlistIndex];
		dispatch(requestWatchlist(
			portfolioId, watchlistTabs.portfolio, currentCurrency, {}, isRecentQuotes
		));
	}, [watchlists, selectedWatchlistIndex]);

	const watchlistOptions = watchlists.length > 0
		? watchlists.map(item => {
			return ({
				active: (activeWatchlistId === item.portfolioId),
				key: item.portfolioId,
				label: item.name === '__RECENT__'
					? translate(item.name)
					: decodeURIComponent(item.name)
			});
		})
		: [{
			active: true,
			key: 'no-watchlists',
			label: translate('noWatchLists')
		}];

	const { tier } = user || {};
	const { code } = tier || {};

	let tableRows = map(get(watchlist, 'holdings', []), (holding, index) => {
		const {
			active,
			issueType,
			priceChange,
			priceChangePercent,
			priceLast,
			symbol,
			underlyingSymbol,
			xid
		} = holding;
		const { exchange, classification } = xref[xid] || {};
		const { id } = classification || {};
		const { countryIso } = exchange || {};
		const oldRoute = getRoute(id, countryIso, (underlyingSymbol || symbol), tier);
		const oldRouteParams = split(oldRoute, '/');

		return !active || !oldRouteParams[1] || !oldRouteParams[2] || !oldRouteParams[3]
			? (
				<tr key={`symbol-cell-${index}`}>
					<th className="symbol-cell" scope="row">
						{format.string(symbol)}
					</th>
					<td colSpan="2">
						{ issueType === 'OP' ? translate('expired') : translate('inactive') }
					</td>
				</tr>
			)
			: (
				<tr key={`symbol-cell-${index}`}>
					<th className="symbol-cell" scope="row">
						<a
							role="link"
							tabIndex="0"
							onKeyDown={event => {
								if (event.keyCode === 13) {
									window.mdPageActions.navigateOBR(`${oldRouteParams[1]}-snapshot`, { market: oldRouteParams[2], symbol: oldRouteParams[3] });
								}
							}}
							onClick={() => window.mdPageActions.navigateOBR(`${oldRouteParams[1]}-snapshot`, { market: oldRouteParams[2], symbol: oldRouteParams[3] })}>
							{format.string(symbol)}
							{format.isValidString(symbol) && <span className="sr-only">{translate('details')}</span>}
						</a>
					</th>
					<td className="last-cell">{ format.priceNoSign(priceLast) }</td>
					<td className="change-cell">
						<div>
							<div className={`${format.getColorClass(priceChange)} ${showChangeInDollars ? '' : 'd-none'}`}>
								<ChangeDir value={priceChange} />
								{format.priceWithSign(priceChange)}
							</div>
							<div className={`${format.getColorClass(priceChangePercent)} ${!showChangeInDollars ? '' : 'd-none'}`}>
								<ChangeDir value={priceChangePercent} />
								{format.percentWithSign(priceChangePercent)}
							</div>
						</div>
					</td>
				</tr>
			);
	});

	if (limitResultsToTen) {
		tableRows = tableRows.slice(0, 10);
	}

	if (tableRows.length === 0 && watchlists.length > 1) {
		tableRows = [(
			<tr key="symbol-cell-0">
				{
					watchlist.name === '__RECENT__'
						? <td colSpan="3" className="text-center">{translate('noRecentSymbols')}</td>
						: <td colSpan="3" className="text-center">{translate('noSymbolsInWatchlist', { name: decodeURI(watchlist.name) })}</td>
				}
			</tr>
		)];
	}

	return (
		<div className="my-watch-lists-widget">
			<div className="title-row d-flex justify-content-between">
				<h3 className="h5">{ translate('myWatchLists') }</h3>
				{
					code !== 'CPICE'
						? (
							<a
								role="link"
								tabIndex="0"
								onKeyDown={event => {
									if (event.keyCode === 13) {
										window.mdPageActions.navigateOBR('my-alerts-watch-list');
									}
								}}
								onClick={() => window.mdPageActions.navigateOBR('my-alerts-watch-list')}>
								<Icon aria-hidden iconName="bell" className="" style={{ width: 16, height: 16 }} />
								<span className="sr-only">{translate('manageAlerts')}</span>
							</a>
						) : null
				}
			</div>
			<>
				<SelectDropDown
					altersDataLabel
					label={translate('selectWatchList')}
					hideLabel
					className="watch-list-dropdown"
					menuItems={watchlistOptions}
					selectedIndex={selectedWatchlistIndex}
					selectCallback={setSelectedWatchlistIndex} />
				<div className="detail-list d-flex justify-content-between">
					<a
						role="link"
						tabIndex="0"
						onKeyDown={event => {
							if (event.keyCode === 13) {
								get(watchlists, `${selectedWatchlistIndex}.portfolioId`)
									&& window.mdPageActions.navigateOBR('watch-list-detail', { watchListId: watchlists[selectedWatchlistIndex].portfolioId });
							}
						}}
						onClick={() => {
							get(watchlists, `${selectedWatchlistIndex}.portfolioId`)
								&& window.mdPageActions.navigateOBR('watch-list-detail', { watchListId: watchlists[selectedWatchlistIndex].portfolioId });
						}}
						className="btn btn-link pl-0">
						{translate('detailList')}
					</a>
					<SlideToggle
						screenReaderText={translate(showChangeInDollars ? 'showChangeInPercent' : 'showChangeInDollars')}
						leftItem={{
							label: '$',
							value: true
						}}
						rightItem={{
							label: '%',
							value: false
						}}
						value={showChangeInDollars}
						callback={() => setShowChangeInDollars(!showChangeInDollars)} />
				</div>
				<div>
					{
						fetchingWatchlist ? <Loader /> : null
					}
					<table className="basic-table table">
						<caption className="sr-only">{translate('myWatchLists')}</caption>
						<colgroup>
							<col className="col-width-36" />
							<col className="col-width-28" />
							<col className="col-width-36" />
						</colgroup>
						<thead>
							<tr>
								<th scope="col">{translate('symbolLabel')}</th>
								<th scope="col" className="pr-0">{translate('lastLabel')}</th>
								<th scope="col">{translate('changeContextVariation')}</th>
							</tr>
						</thead>
						<tbody>
							{ tableRows }
						</tbody>
					</table>
				</div>
				{ watchlists.length > 1 || tableRows.length > 0
					? (
						<div className="as-of-date-footer d-flex justify-content-center">
							{translate('quotesDelayedBy15')}
						</div>
					)
					: (
						<div className="no-watchlists-long d-flex justify-content-center">
							{translate('noWatchListsLong')}
						</div>
					)}

				<div className="d-flex justify-content-center">
					{
						watchlists.length > 1 || tableRows.length > 0
							? (
								<button
									type="button"
									aria-live="polite"
									className={`btn btn-link more-button p-0 ${get(watchlist, 'holdings.length') <= 10 ? 'd-none' : ''}`}
									onClick={() => setLimitResultsToTen(!limitResultsToTen)}>
									{limitResultsToTen ? translate('more') : translate('less')}
									<Icon className={limitResultsToTen ? 'chevron-down' : 'chevron-up'} iconName="chevron" style={{ width: 14 }} />
								</button>
							)
							: (
								<a
									role="link"
									tabIndex="0"
									onKeyDown={event => {
										if (event.keyCode === 13) {
											window.mdPageActions.navigateOBR('watch-lists');
										}
									}}
									onClick={() => window.mdPageActions.navigateOBR('watch-lists')}
									className="btn btn-link p-0 no-watchlists-link">
									{translate('noWatchListsLongLink')}
								</a>
							)
					}
				</div>
			</>
		</div>
	);
};

export default withTranslate(MyWatchListsWidget);
